/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import type {
  DomainToken,
  ProvisionDevicesResponseV2
} from "@tivocorp/data-domain-lib";

import type { SignInErrorPayload, SignInState } from "../signIn.types";
import { SIGN_IN_FALLBACK_ERROR_CODE } from "../common/authErrors";
import { signInWithCredentials } from "../thunks/credentialsSignIn.thunks";

/**
 * Reducers for the interactive user-id / password sign-in flow.
 */
export function registerCredentialsSignInReducers(
  builder: ActionReducerMapBuilder<SignInState>
): void {
  builder.addCase(signInWithCredentials.pending, (state) => {
    state.isSigningIn = true;
    state.signInError = "";
    state.signInErrorCode = "";
    state.signInErrorServiceCode = "";
  });
  builder.addCase(
    signInWithCredentials.fulfilled,
    (
      state,
      action: PayloadAction<{
        domainToken: DomainToken;
        provisionResponse: ProvisionDevicesResponseV2;
      }>
    ) => {
      // Keep isSigningIn true so inputs stay locked until the screen
      // unmounts and resetSignInState clears the flag.
      state.signInError = "";
      state.signInErrorCode = "";
      state.domainToken = action.payload.domainToken;
      state.provisionResponse = action.payload.provisionResponse;
    }
  );
  builder.addCase(signInWithCredentials.rejected, (state, action) => {
    state.isSigningIn = false;
    const payload: SignInErrorPayload | undefined = action.payload;
    if (payload !== undefined && payload !== null) {
      state.signInError = payload.message;
      state.signInErrorCode = payload.code;
      state.signInErrorServiceCode = payload.serviceCode;
      return;
    }
    // No payload means the thunk threw outside its own catch (or was aborted),
    // so there is no classified error to read — report the catch-all rather
    // than leaving the view with a code it cannot route on.
    state.signInError = "Sign-in failed";
    state.signInErrorCode = SIGN_IN_FALLBACK_ERROR_CODE;
    state.signInErrorServiceCode = "";
  });
}
